import { EnvironmentDetail, RegionMappingService } from '@amzn/ihm-associate-tools-js-lib'
import { getActiveStoreRegion, getStoreRegion } from '@services/StoreRegionRegistry'

/**
 * Region resolution for a session that can span cells. See "Region and Cell Routing" in AGENTS.md
 * for the two questions this answers separately ("which cell holds this tenant" vs. "which cell owns
 * the store we are reading") and for the naming convention: a "region" is an airport code, a "realm"
 * is an AWS region id.
 *
 * Deliberately *not* pinning North America to IAD. That pin existed while IAD was the only live
 * prod cell and was removed from the app in CR-298723936 once prod PDX went live: a US tenant can
 * legitimately be in either cell, so pinning re-breaks the tenants the fallback fixed.
 */

/** Airport codes for every cell the platform is deployed to. */
export const AIRPORT_CODES = ['iad', 'pdx', 'dub', 'sin'] as const

/**
 * Realms a US tenant can be homed in. Only US realms are listed: EU and AP were never pinned
 * this way, and listing them would make every DUB or SIN miss pay for pointless peer lookups.
 */
export const US_REALMS: readonly string[] = ['us-east-1', 'us-west-2']

/** Matches an AWS region id, e.g. us-west-2, ap-southeast-1. */
const REALM_PATTERN = /^[a-z]{2}-[a-z]+-\d+$/

/**
 * Both RegionMappingService conversions throw on input they do not recognise rather than
 * returning undefined, and callers here routinely hold a region of unverified provenance (a
 * gateway response, localStorage, a URL param), so both wrappers swallow that.
 *
 * Both are also tolerant of already-converted input, because region strings have historically
 * been passed around in either form.
 */

/** The AWS region id for an airport code, or undefined when it maps to no known cell. */
export const toRealm = (region: string | undefined): string | undefined => {
    if (!region) {
        return undefined
    }
    const value = region.toLowerCase()
    if (REALM_PATTERN.test(value)) {
        return value
    }
    try {
        return RegionMappingService.getAwsRegionFromAirportCode(value)?.toLowerCase()
    } catch {
        return undefined
    }
}

/**
 * The airport code for an AWS region id, or undefined when it maps to no cell this platform is
 * deployed to.
 *
 * Validates against AIRPORT_CODES rather than passing unrecognised values through, because the
 * result is used to build Coral qualifiers and pick tool endpoints. A store's region arrives from
 * PSCK (uppercase, e.g. "PDX"), so an unexpected value there should degrade to the cell we are
 * already pinned to rather than produce a qualifier no client config can satisfy.
 */
export const toAirportCode = (region: string | undefined): string | undefined => {
    if (!region) {
        return undefined
    }
    const value = region.toLowerCase()
    if (REALM_PATTERN.test(value)) {
        try {
            return validAirportCode(RegionMappingService.getAirportCodeFromAwsRegion(value))
        } catch {
            return undefined
        }
    }
    return validAirportCode(value)
}

const validAirportCode = (code: string | undefined): string | undefined => {
    const value = code?.toLowerCase()
    return AIRPORT_CODES.includes(value as typeof AIRPORT_CODES[number]) ? value : undefined
}

/**
 * The cell this session is pinned to: the one in the landing URL, which the app already sets to
 * the tenant's serving cell after its own domain lookup. This is the starting point, not the
 * answer — a web session has had no such lookup done for it.
 */
export const getPinnedAirportCode = (): string => toAirportCode(EnvironmentDetail.getRegion()) ?? 'pdx'

/** As getPinnedAirportCode, as an AWS region id. */
export const getPinnedRealm = (): string | undefined => toRealm(getPinnedAirportCode())

/**
 * Realms to retry when a lookup misses in the pinned realm, pinned realm excluded.
 *
 * Empty unless the pinned realm is a US realm, so EU and AP tenants make zero extra calls.
 */
export const getPeerRealms = (): string[] => {
    const pinned = getPinnedRealm()
    if (!pinned || !US_REALMS.includes(pinned)) {
        return []
    }
    return US_REALMS.filter((realm) => realm !== pinned)
}

/**
 * The cell whose resources this session should use: the active store's cell once known,
 * otherwise the cell we are pinned to.
 */
export const getActiveAirportCode = (): string =>
    toAirportCode(getActiveStoreRegion()) ?? getPinnedAirportCode()

/** As getActiveAirportCode, as an AWS region id. */
export const getActiveRealm = (): string | undefined => toRealm(getActiveAirportCode())

/**
 * The cell owning one specific store, or undefined while that is unknown.
 *
 * Unlike getActiveAirportCode this does not fall back, because callers use it to decide whether a
 * store differs from the active one: a fallback would make every unrecorded store look like it
 * shares the session's cell.
 */
export const getAirportCodeForStore = (storeId: string | undefined): string | undefined =>
    toAirportCode(getStoreRegion(storeId))

/** As getAirportCodeForStore, as an AWS region id. Does not fall back, for the same reason. */
export const getRealmForStore = (storeId: string | undefined): string | undefined =>
    toRealm(getAirportCodeForStore(storeId))
